<template>
	<div class="editor-container">
		<!-- Questo è il DIV in cui Rete.js disegnerà il tuo grafo -->
		<!-- input nascosto: se vuoi visualizzarlo comunque, spostalo fuori -->
		<!-- ▸ BREADCRUMB ------------------------------------------------- -->
		<header id="editor-form" class="breadcrumb">
			<span>{{ deviceName || "Device senza nome" }}</span>

			<div class="breadcrumb-actions">
				<ViewToggle id="toggle-button" class="toggle-icon" />
				<NotificationBell v-if="showCrumbBell" placement="header" />
				<button
					id="exit-to-devices"
					class="btn-exit"
					@click="router.push('/devices')"
				>
					Esci
				</button>
			</div>
		</header>

		<input v-model="deviceName" type="hidden" />
		<div ref="container" class="rete-background" id="add-modules-anchor">
			<span
				id="add-modules-anchor-pin"
				class="tour-pin"
				aria-hidden="true"
			></span>
		</div>
		<!--  pop-up ------------------------------------------------------------------>
		<div v-if="showNameModal" class="modal-mask">
			<div class="modal">
				<h3>Nome del device</h3>

				<input
					v-model="tempName"
					class="modal-input"
					placeholder="Es. Weather-Station-01"
				/>

				<div class="modal-actions">
					<button @click="confirmName">OK</button>
					<button @click="showNameModal = false">Annulla</button>
				</div>
			</div>
		</div>
		<!-- Overlay di salvataggio migliorato -->
		<transition name="fade" mode="out-in">
			<div v-if="loader" class="loader-overlay" key="loader">
				<div class="loader-content">
					<div class="loader-spinner"></div>
					<span>Salvataggio in corso...</span>
				</div>
			</div>
		</transition>
	</div>
</template>

<script setup lang="ts">
import axios from "@/axiosInstance";
import { API_URL } from "@/config";
import { applyGraphSpec, type GraphSpec } from "@/rete/applyGraphSpec";
import {
	Connection,
	createEditor,
	LORa,
	LTE,
	MotherBoard,
	normalizeLabel,
	SAT,
	type Node as ReteNode,
	type Schemes,
	PLC,
} from "@/rete/default";
import router from "@/router";
import { debounce } from "lodash";
import { AreaExtensions, AreaPlugin } from "rete-area-plugin";
import type { Ref } from "vue";
import {
	computed,
	inject,
	nextTick,
	onBeforeUnmount,
	onMounted,
	ref,
	watch,
} from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";
import NotificationBell from "./NotificationBell.vue";
import ViewToggle from "./ViewToggle.vue";

const isDesktop = inject<Ref<boolean>>("isDesktop")!;
const isSidebarOpen = inject<Ref<boolean>>("isSidebarOpen")!;

const showCrumbBell = computed(
	() => !isDesktop.value || (isDesktop.value && !isSidebarOpen.value),
);

/* ------------------------ stato pendente ------------------------ */
const pendingGraphSpec = ref<GraphSpec | null>(null);

/* -------------------------- stato editor -------------------------- */
const route = useRoute();
const toast = useToast();
const container = ref<HTMLElement | null>(null);
let area: InstanceType<typeof AreaPlugin>;
let editor: any;
let layout: (scale?: number) => Promise<void>;
let destroy: () => void;

let loadingGraph = false;
const loader = ref(false);
const saveCompleted = ref(false);

/* ------------------------- stato device UI ------------------------ */
const deviceName = ref("");
const tempName = ref("");
const showNameModal = ref(false);

/* ------------------------- readiness & queue ---------------------- */
let ready = false;
const pending: any[] = []; // comandi ai arrivati prima della readiness
let lastLoad = 0;

/* ----------------------------- utils ------------------------------ */
// helper: attende che la view dei nodi esista (evita l’errore ELK)
async function waitViewsReady(check: () => boolean, tries = 20) {
	for (let i = 0; i < tries; i++) {
		await nextTick();
		await new Promise((r) => requestAnimationFrame(r));
		if (check()) return;
	}
}

type AppIntent =
	| "createConnection"
	| "addNewNode"
	| "removeConnection"
	| "removeNode"
	| "moveNode"
	| "addNewDevice"
	| "other";

function nodeFactory(type: string) {
	if (type === "MotherBoard") return new MotherBoard();
	if (type === "SAT") return new SAT();
	if (type === "LORa") return new LORa();
	if (type === "LTE" || type === "LTE/5G") return new LTE();
	if (type === "PLC") return new PLC();
	return null;
}

// function resolveNode(by: "id" | "label", value: string): any | null {
//   const nodes = editor.getNodes() as ReteNode[];
//   if (by === "id") {
//     return nodes.find((n: any) => n.id === value) || null;
//   }
//   // by label
//   const matches = nodes.filter((n: any) => n.label === value);
//   if (matches.length > 1) {
//     toast.warning(`Più nodi con label "${value}". Specifica l'id.`, {
//       position: "top-right",
//     });
//     return null;
//   }
//   return matches[0] || null;
// }
function resolveNode(by: "id" | "label", value: string): any | null {
	const nodes = editor.getNodes() as ReteNode[];

	if (by === "id") {
		return nodes.find((n: any) => n.id === value) || null;
	}

	// 1) match esatto
	let matches = nodes.filter((n: any) => n.label === value);
	if (matches.length === 1) return matches[0];
	if (matches.length > 1) {
		toast.warning(`Più nodi con label "${value}". Specifica l'id.`, {
			position: "top-right",
		});
		return null;
	}

	// 2) match case-insensitive
	const lower = String(value).toLowerCase();
	matches = nodes.filter((n: any) => String(n.label).toLowerCase() === lower);
	if (matches.length === 1) return matches[0];
	if (matches.length > 1) {
		toast.warning(`Più nodi con label simile a "${value}". Specifica l'id.`, {
			position: "top-right",
		});
		return null;
	}

	// 3) match canonicalizzato (es. "motherboard" -> "MotherBoard", "lte" -> "LTE/5G")
	const canonical = normalizeLabel(value);
	if (canonical && canonical !== value) {
		matches = nodes.filter((n: any) => n.label === canonical);
		if (matches.length === 1) return matches[0];
		if (matches.length > 1) {
			toast.warning(`Più nodi con label "${canonical}". Specifica l'id.`, {
				position: "top-right",
			});
			return null;
		}
	}

	return null;
}

// function switchView(view: "editor" | "data") {
// 	const name = view === "editor" ? "Editor" : "Data";
// 	router.push({ name, params: { deviceId: deviceIdComputed.value } });
// }

/* -------------------------- salvataggio --------------------------- */
const saveDebounced = debounce(trySave, 3000, {
	leading: false,
	trailing: true,
});

function trySave() {
	deviceName.value.trim()
		? saveFullGraph(Number(route.params.deviceId))
		: (showNameModal.value = true);
}

const confirmName = () => {
	if (!tempName.value.trim()) return;
	deviceName.value = tempName.value.trim();
	showNameModal.value = false;
	saveFullGraph(Number(route.params.deviceId));
};

const saveFullGraph = async (id: number) => {
	loader.value = true;
	saveCompleted.value = false;
	try {
		const name = deviceName.value;
		const nodes = editor.getNodes().map((n: any) => ({
			id: n.id,
			label: n.label,
			x: area.nodeViews.get(n.id)!.position.x || 0,
			y: area.nodeViews.get(n.id)!.position.y || 0,
		}));
		const connections = editor.getConnections().map((c: any) => ({
			id: c.id,
			source: c.source,
			sourceOutput: c.sourceOutput,
			target: c.target,
			targetInput: c.targetInput,
		}));

		await axios.post(`${API_URL}/devices/${id}/create-graph`, {
			name,
			nodes,
			connections,
		});

		saveCompleted.value = true;
		setTimeout(() => {
			toast.info(`Salvataggio del device '${id}' riuscito`, {
				position: "top-right",
				duration: 2000,
			});
		}, 200);
	} catch (error: any) {
		if (error.response?.status === 401) {
			toast.error("Non autenticato. Reindirizzo al login…", {
				position: "top-right",
			});
			router.push("/auth");
		} else {
			toast.error("Errore durante il salvataggio", { position: "top-right" });
		}
	} finally {
		// Nascondi il loader solo dopo che il toast è stato mostrato
		if (saveCompleted.value) {
			setTimeout(() => {
				loader.value = false;
			}, 1000); // Mantiene il loader visibile per almeno 1 secondo dopo il successo
		} else {
			loader.value = false;
		}
	}
};

/* ------------------------ caricamento grafo ----------------------- */
const loadGraph = async (id: number) => {
	lastLoad = Date.now();
	if (Number.isNaN(id) || id <= 0) {
		toast.error("Device non valido", { position: "top-right" });
		router.replace("/devices");
		return;
	}

	loadingGraph = true;
	try {
		const { data: graph } = await axios.get(
			`${API_URL}/devices/${id}/get-graph`,
		);

		deviceName.value = graph.name ?? "";
		tempName.value = deviceName.value;

		// map label → ctor
		type NodeLabel = "MotherBoard" | "SAT" | "LORa" | "LTE/5G" | "PLC";
		type NodeCtor = new () => ReteNode;
		const ctorMap: Record<NodeLabel, NodeCtor> = {
			MotherBoard,
			SAT,
			LORa,
			"LTE/5G": LTE,
			PLC,
		};

		const isValid =
			graph && Array.isArray(graph.nodes) && Array.isArray(graph.connections);
		if (!isValid) return;

		// pulisci editor
		editor.clear();
		await nextTick();

		// ricrea nodi
		const nodeMap: Record<string, ReteNode> = {};
		for (const n of graph.nodes) {
			const Ctor = ctorMap[n.label as NodeLabel];
			if (!Ctor) continue;
			const node = new Ctor();
			node.id = n.id;
			await editor.addNode(node);
			nodeMap[n.id] = node;

			await nextTick();
			const view = area.nodeViews.get(n.id);
			view?.translate(n.x, n.y);
		}

		// ricrea connessioni
		for (const c of graph.connections) {
			const source = nodeMap[c.source_node_id];
			const target = nodeMap[c.target_node_id];
			if (!source || !target) continue;

			const conn = new Connection(
				source,
				c.source_output,
				target,
				c.target_input,
			);
			conn.id = c.id;
			await editor.addConnection(conn);
		}

		await nextTick();
		const allNodes = editor.getNodes();
		if (allNodes.length) {
			await AreaExtensions.zoomAt(area, allNodes, {
				scale: window.matchMedia("(pointer:coarse)").matches ? 0.8 : 0.6,
			});
		}
	} catch (e) {
		toast.error("Errore nel caricamento del grafo", { position: "top-right" });
	} finally {
		loadingGraph = false;
	}
};

function defaultCenterPosition() {
	const { x, y, k } = (area as any).area.transform; // trasformazione corrente
	const rect = (area as any).container.getBoundingClientRect();
	// converti centro viewport da schermo → coordinate grafiche
	const cx = (rect.width / 2 - x) / k;
	const cy = (rect.height / 2 - y) / k;
	return { x: Math.round(cx), y: Math.round(cy) };
}

// Trova il primo slot libero vicino a start evitando sovrapposizioni grossolane
function pickFreeSpotNear(
	area: any,
	editor: any,
	start: { x: number; y: number },
) {
	const NODE_W = 220; // fallback: larghezza stimata nodo
	const NODE_H = 160; // fallback: altezza stimata nodo
	const STEP = 240; // distanza minima tra centri
	const MAX_R = 6; // quante “corone” provare

	const rects = [...editor.getNodes()].map((n: any) => {
		const v = area.nodeViews.get(n.id);
		const w = v?.element?.offsetWidth ?? NODE_W;
		const h = v?.element?.offsetHeight ?? NODE_H;
		return { x: v?.position.x ?? 0, y: v?.position.y ?? 0, w, h };
	});

	// dato un top-left del nuovo nodo, collide?
	const collides = (x: number, y: number, w = NODE_W, h = NODE_H) =>
		rects.some(
			(r) =>
				!(x + w <= r.x || r.x + r.w <= x || y + h <= r.y || r.y + r.h <= y),
		);

	// partiamo dal centro (top-left ricavato dai centri)
	let candidate = {
		x: Math.round(start.x - NODE_W / 2),
		y: Math.round(start.y - NODE_H / 2),
	};
	if (!collides(candidate.x, candidate.y)) return candidate;

	// piccola spirale a passi STEP
	for (let r = 1; r <= MAX_R; r++) {
		for (const [dx, dy] of [
			[r, 0],
			[0, r],
			[-r, 0],
			[0, -r],
			[r, r],
			[-r, r],
			[r, -r],
			[-r, -r],
		]) {
			const x = candidate.x + dx * STEP;
			const y = candidate.y + dy * STEP;
			if (!collides(x, y)) return { x, y };
		}
	}
	// fallback: lascia il default
	return candidate;
}

async function applyGraphSpecFallback(spec: GraphSpec) {
	// crea tutti i nodi e conserva gli alias
	const aliasMap = new Map<string, ReteNode>();
	for (const n of spec.nodes ?? []) {
		const node = nodeFactory(n.type);
		if (!node) continue;
		await editor.addNode(node);
		await nextTick();

		// posizione esplicita o default anti-overlap
		const view = area.nodeViews.get(node.id);
		if (n.position?.x != null && n.position?.y != null) {
			view?.translate(n.position.x, n.position.y);
		} else {
			const p0 = defaultCenterPosition();
			const { x, y } = pickFreeSpotNear(area, editor, p0);
			view?.translate(x, y);
		}

		aliasMap.set(n.alias, node);
	}

	// connessioni
	for (const c of spec.connections ?? []) {
		const src = aliasMap.get(c.from);
		const dst = aliasMap.get(c.to);
		if (!src || !dst) continue;
		await editor.addConnection(
			new Connection(src, c.out ?? "value", dst, c.in ?? "value"),
		);
	}
}

/* ------------------------ handler comandi AI ---------------------- */
type ClientInstructions = {
	context:
		| "Editor"
		| "Dashboard"
		| "Data"
		| "Devices"
		| "Account"
		| "Other"
		| null;
	intent: AppIntent;
	params: Record<string, any>;
	missing: string[];
};

function canonicalizeInstruction(ci: any) {
	if (!ci || !ci.params) return ci;

	// normalizza type
	if (ci.intent === "addNewNode" && typeof ci.params.type === "string") {
		ci.params.type = normalizeLabel(ci.params.type);
	}
	if (ci.intent === "addNewNodeMany" && Array.isArray(ci.params.types)) {
		ci.params.types = ci.params.types.map((t: string) => normalizeLabel(t));
	}

	// normalizza label in connessioni se by:"label"
	if (ci.intent === "createConnection") {
		if (
			ci.params?.source?.by === "label" &&
			typeof ci.params.source.value === "string"
		) {
			ci.params.source.value = normalizeLabel(ci.params.source.value);
		}
		if (
			ci.params?.target?.by === "label" &&
			typeof ci.params.target.value === "string"
		) {
			ci.params.target.value = normalizeLabel(ci.params.target.value);
		}
	}

	if (ci.intent === "removeConnection") {
		if (
			ci.params?.source?.by === "label" &&
			typeof ci.params.source.value === "string"
		) {
			ci.params.source.value = normalizeLabel(ci.params.source.value);
		}
		if (
			ci.params?.target?.by === "label" &&
			typeof ci.params.target.value === "string"
		) {
			ci.params.target.value = normalizeLabel(ci.params.target.value);
		}
	}

	// normalizza removeNode by:"label"
	if (
		ci.intent === "removeNode" &&
		ci.params?.by === "label" &&
		typeof ci.params.value === "string"
	) {
		ci.params.value = normalizeLabel(ci.params.value);
	}

	return ci;
}

function resolveConnectionsBetween(
	srcNodeId: string,
	dstNodeId: string,
	opts?: {
		out?: string;
		in?: string;
		direction?: "source->target" | "target->source" | "any";
	},
) {
	const out = opts?.out ?? "value";
	const inp = opts?.in ?? "value";
	const dir = opts?.direction ?? "any";

	const all = editor.getConnections() as Array<{
		id: string;
		source: string;
		sourceOutput: string;
		target: string;
		targetInput: string;
	}>;

	const matchFwd = (c: any) =>
		c.source === srcNodeId &&
		c.target === dstNodeId &&
		c.sourceOutput === out &&
		c.targetInput === inp;
	const matchRev = (c: any) =>
		c.source === dstNodeId &&
		c.target === srcNodeId &&
		c.sourceOutput === out &&
		c.targetInput === inp;

	if (dir === "source->target") return all.filter(matchFwd);
	if (dir === "target->source") return all.filter(matchRev);
	// any: prova entrambe
	return all.filter((c) => matchFwd(c) || matchRev(c));
}

async function handleAIResult(result: any) {
	const ops: any[] =
		Array.isArray(result?.operations) && result.operations.length
			? result.operations
			: result?.clientInstructions
			? [result.clientInstructions]
			: [];

	if (!ops.length) return;

	const normalized = ops.map(canonicalizeInstruction);
	// console.log("normalized", normalized);

	// Se la prima op ha deviceId diverso, naviga e carica il grafo
	const firstWithId = normalized.find((o) => o?.params?.deviceId);
	if (
		firstWithId &&
		String(route.params.deviceId) !== String(firstWithId.params.deviceId)
	) {
		await router.push(`/editor/${firstWithId.params.deviceId}?skipDefaults=1`);
		await nextTick();
		await loadGraph(Number(firstWithId.params.deviceId));
	}

	// Esecuzione in batch: niente save/layout dentro handleInstruction
	for (const ci of normalized) {
		// console.log("ci", ci);
		await handleInstruction(ci as any, { batch: true });
	}

	await nextTick();
	// try {
	//   await layout(0.7);
	// } catch {}
	const hasMove = normalized.some((o) => o?.intent === "moveNode");
	if (!hasMove) {
		try {
			await layout(0.7);
		} catch {}
	}
	saveDebounced();
}

type HandleOpts = { batch?: boolean };

async function handleInstruction(
	ci: ClientInstructions,
	opts: HandleOpts = {},
) {
	if (ci.context !== "Editor") return;

	// chiedi slot mancanti
	if (ci.missing?.length) {
		toast.warning(`Servono più dati: ${ci.missing.join(", ")}`, {
			position: "top-right",
		});
		return;
	}

	const intent = ci.intent;

	// 0) assicurati di essere nel device giusto (se fornito)
	const deviceIdParam = ci.params?.deviceId;
	if (
		deviceIdParam &&
		String(route.params.deviceId) !== String(deviceIdParam)
	) {
		await router.push(`/editor/${deviceIdParam}?skipDefaults=1`);
		// lascia che il watcher ricarichi il grafo
	}

	// 1) addNewDevice con graph (catene/rami) gestito tramite AiPrompt
	if (intent === "addNewDevice") {
		return;
	}

	// 2) addNewNode
	if (intent === "addNewNode") {
		const node = nodeFactory(ci.params?.type);
		if (!node) {
			toast.error("Tipo nodo non valido", { position: "top-right" });
			return;
		}
		await editor.addNode(node);
		// console.log("[AI] Aggiunto nuovo nodo:", node.id, "type:", node.label);
		await nextTick();

		// posizione opzionale
		const view = area.nodeViews.get(node.id);
		const pos = ci.params?.position;

		if (pos?.x != null && pos?.y != null) {
			view?.translate(pos.x, pos.y);
		} else {
			// Nessuna posizione: usa default + anti-overlap
			const p0 = defaultCenterPosition();
			const { x, y } = pickFreeSpotNear(area, editor, p0);
			view?.translate(x, y);
		}

		if (!opts.batch) {
			try {
				await layout(0.7);
			} catch {}
			saveDebounced();
		}
		return;
	}

	// 3) createConnection
	if (intent === "createConnection") {
		const s = ci.params?.source;
		const t = ci.params?.target;
		if (!s || !t) {
			toast.error("source/target mancanti", {
				position: "top-right",
				duration: 2000,
			});
			return;
		}
		const src = resolveNode(s.by, s.value);
		const dst = resolveNode(t.by, t.value);
		if (!src || !dst) {
			const miss = [
				!src ? `sorgente "${s.value}"` : null,
				!dst ? `destinazione "${t.value}"` : null,
			]
				.filter(Boolean)
				.join(" e ");
			toast.info(
				`Nodo ${miss} non trovato nel device corrente, occorre aggiungerlo prima.`,
				{ position: "top-right", duration: 3000 },
			);
			return;
		}

		await editor.addConnection(
			new Connection(src, s.output ?? "value", dst, t.input ?? "value"),
		);
		if (!opts.batch) {
			try {
				await layout(0.7);
			} catch {}
			saveDebounced();
		}
		return;
	}

	if (intent === "removeConnection") {
		const s = ci.params?.source;
		const t = ci.params?.target;
		const direction: "source->target" | "target->source" | "any" =
			ci.params?.direction ?? "any";

		if (!s || !t) {
			toast.error("source/target mancanti per removeConnection", {
				position: "top-right",
			});
			return;
		}

		const src = resolveNode(s.by, s.value);
		const dst = resolveNode(t.by, t.value);
		if (!src || !dst) {
			const miss = [
				!src ? `sorgente "${s.value}"` : null,
				!dst ? `destinazione "${t.value}"` : null,
			]
				.filter(Boolean)
				.join(" e ");
			toast.info(`Nodo ${miss} non trovato nel device corrente.`, {
				position: "top-right",
				duration: 2500,
			});
			return;
		}

		const conns = resolveConnectionsBetween(src.id, dst.id, {
			out: s.output ?? "value",
			in: t.input ?? "value",
			direction,
		});

		if (!conns.length) {
			toast.info("Nessuna connessione trovata tra i due nodi.", {
				position: "top-right",
				duration: 2200,
			});
			return;
		}

		for (const c of conns) {
			await editor.removeConnection(c.id);
		}
		if (!opts.batch) {
			await nextTick();
			try {
				await layout(0.7);
			} catch {}
			saveDebounced();
		}
		return;
	}

	// 4) removeNode
	// if (intent === "removeNode") {
	//   const by = ci.params?.by;
	//   const value = ci.params?.value;
	//   if (!by || !value) {
	//     toast.error("by/value mancanti per removeNode", {
	//       position: "top-right",
	//     });
	//     return;
	//   }

	//   if (value === "all" || value === "allNodes") {
	//     console.log("entro qui");
	//     // cancella tutti i nodi
	//     const connections = editor.getConnections();
	//     for (const c of connections) {
	//       await editor.removeConnection(c.id);
	//     }
	//     const nodes = editor.getNodes();
	//     for (const n of nodes) {
	//       await editor.removeNode(n.id);
	//     }
	//     // console.log("connections", connections);
	//     if (!opts.batch) {
	//       await nextTick();
	//       try {
	//         await layout(0.7);
	//       } catch {}
	//       saveDebounced();
	//     }
	//     return;
	//   }

	//   const connections = editor.getConnections();
	//   console.log("connections", connections);

	//   connections.map(
	//     async (c: { source: string; target: string; id: string }) => {
	//       const src = c.source;
	//       const dst = c.target;
	//       if (src === node.id || dst === node.id) {
	//         await editor.removeConnection(c.id);
	//       }
	//       console.log("connessione eliminata", c.id);
	//     }
	//   );
	//   const node = resolveNode(by, value);
	//   console.log("node", node);
	//   if (!node) return;
	//   await editor.removeNode(node.id);
	//   if (!opts.batch) {
	//     await nextTick();
	//     try {
	//       await layout(0.7);
	//     } catch {}
	//     saveDebounced();
	//   }
	//   return;
	// }
	// 4) removeNode
	if (intent === "removeNode") {
		const by = ci.params?.by as "id" | "label" | "type";
		const value = ci.params?.value as string;

		if (!by || !value) {
			toast.error("by/value mancanti per removeNode", {
				position: "top-right",
			});
			return;
		}

		// --- A) Rimuovi TUTTO (all/allNodes)
		if (value === "all" || value === "allNodes") {
			// copia per non iterare su array "live"
			const connections = [...editor.getConnections()];
			for (const c of connections) {
				await editor.removeConnection(c.id);
			}
			const nodes = [...editor.getNodes()];
			for (const n of nodes) {
				await editor.removeNode(n.id);
			}
			if (!opts.batch) {
				await nextTick();
				try {
					await layout(0.7);
				} catch {}
				saveDebounced();
			}
			return;
		}

		// --- B) Rimuovi per "type" = rimuovi TUTTI i nodi con quella label/type
		if (by === "type") {
			const toRemove = (editor.getNodes() as any[]).filter(
				(n) => n.label === value,
			);
			if (!toRemove.length) return;

			// rimuovi prima le connessioni che toccano uno dei nodi
			const connections = [...editor.getConnections()];
			const ids = new Set(toRemove.map((n) => n.id));
			for (const c of connections) {
				if (ids.has(c.source) || ids.has(c.target)) {
					await editor.removeConnection(c.id);
				}
			}
			for (const n of toRemove) {
				await editor.removeNode(n.id);
			}

			if (!opts.batch) {
				await nextTick();
				try {
					await layout(0.7);
				} catch {}
				saveDebounced();
			}
			return;
		}

		// --- C) Rimuovi singolo per id/label
		// risolvi PRIMA il nodo, POI usa node.id
		const node = resolveNode(by as any, value);
		if (!node) return;

		// togli connessioni agganciate a quel nodo
		const connections = [...editor.getConnections()];
		for (const c of connections) {
			if (c.source === node.id || c.target === node.id) {
				await editor.removeConnection(c.id);
			}
		}
		await editor.removeNode(node.id);

		if (!opts.batch) {
			await nextTick();
			try {
				await layout(0.7);
			} catch {}
			saveDebounced();
		}
		return;
	}

	// 5) moveNode
	if (intent === "moveNode") {
		const by = ci.params?.by;
		const value = ci.params?.value;
		const x = ci.params?.x;
		const y = ci.params?.y;
		if (!by || !value || x == null || y == null) {
			toast.error("Parametri mancanti per moveNode", { position: "top-right" });
			return;
		}
		const node = resolveNode(by, value);
		if (!node) return;

		const view = area.nodeViews.get(node.id);
		view?.translate(x, y);
		if (!opts.batch) {
			try {
				await layout(0.7);
			} catch {}
			saveDebounced();
		}
		return;
	}
}

/* ---------------------- listener evento AI ------------------------ */
// 🔔 Listener che accetta sia singolo oggetto sia array/operations
function onAiEditorCommand(e: Event) {
	const payload = (e as CustomEvent).detail;
	// console.log("[AI] raw payload →", JSON.stringify(payload, null, 2));

	if (Array.isArray(payload)) {
		// console.log("arrivo qui?1");

		handleAIResult({ operations: payload });
	} else if (payload?.operations || payload?.clientInstructions) {
		// console.log("arrivo qui?2");
		handleAIResult(payload);
	} else if (payload?.context) {
		// console.log("arrivo qui?3");
		handleAIResult({ clientInstructions: payload });
	}
}
// function onAiEditorCommand(e: Event) {
//   const ci = (e as CustomEvent).detail as ClientInstructions;
//   if (!ready) {
//     pending.push(ci);
//     return;
//   }
//   // console.log("[AI] onAiEditorCommand intent:", ci.intent, "params:", ci.params);
//   handleInstruction(ci);
// }

// listener che richiama loadGraph e poi applica lo spec (se presente)
async function onAiEditorRefresh(e: Event) {
	lastLoad = Date.now();
	// console.log("lastload dentro onaieditorrefresh", lastLoad);
	const d = (e as CustomEvent).detail || {};
	const id = d.deviceId ?? route.params.deviceId;
	if (!id) return;

	// se lo spec arriva dall'evento, salvalo qui
	if (d.graphSpec) pendingGraphSpec.value = d.graphSpec;
	if (d.name) deviceName.value = d.name;

	await loadGraph(Number(id));

	const spec = pendingGraphSpec.value;
	// console.log("[AI] onAiEditorRefresh spec:", spec);
	if (!spec) return;

	try {
		editor.clear();
		await nextTick();

		try {
			await applyGraphSpec(editor, area as AreaPlugin<Schemes>, spec);
		} catch (err) {
			console.warn("applyGraphSpec ha fallito, fallback:", err);
			await applyGraphSpecFallback(spec);
		}

		await waitViewsReady(() => editor.getNodes().length > 0);
		await nextTick();
		await new Promise((r) => requestAnimationFrame(r));

		try {
			await layout(0.7);
		} catch {
			await new Promise((r) => requestAnimationFrame(r));
			await layout(0.7);
		}

		// await saveFullGraph(Number(id));
	} catch (err) {
		console.error(" apply/layout/save error:", err);
		toast.error("Errore nell’applicazione del grafo AI.", {
			position: "top-right",
		});
	} finally {
		pendingGraphSpec.value = null;
	}
}

/* ------------------------------ mount ----------------------------- */
onMounted(async () => {
	window.addEventListener(
		"ai-editor-refresh",
		onAiEditorRefresh as EventListener,
	);

	const skipDefaults = route.query.skipDefaults === "1";
	({ editor, area, layout, destroy } = await createEditor(
		container.value!,
		skipDefaults,
	));

	let draggingNode = false;

	area.addPipe((ctx: any) => {
		if (loadingGraph) return ctx; // ignora tutti gli eventi durante il reload
		// se inizio un pointerdown, controllo se il target è dentro un nodo
		if (ctx.type === "pointerup" || ctx.type === "pointerdown") {
			const el = ctx.data.event.target as Node;
			draggingNode = Array.from(area.nodeViews.values()).some((view) =>
				view.element.contains(el),
			);
		}
		// al rilascio, salvo **solo** se draggingNode === true
		if (draggingNode) {
			saveDebounced();
		}
		draggingNode = false;
		// }
		return ctx;
	});

	// 5. Auto-save usando la pipeline di Rete.js
	editor.addPipe((ctx: any) => {
		// console.log("lastload dentro addpipe", lastLoad);
		// console.log("datenow-lastload", Date.now() - lastLoad);
		if (loadingGraph) return ctx; // ignora tutti gli eventi durante il reload
		// Se vengono registrati eventi con ctx.type involontari, in questo caso 'removed', non appena uno entra nell'editor
		// così come stava seccedendo, allora non salviamo. Infatti lastLoad prende il valore di date.now dentro:
		// onAiEditorRefresh, loadGraph e durante il richiamo di loadGraph quando entriamo nella vue tramite deviceId.
		// Forse da riadattare questa soluzione con qualcosa di più sicuro ma attualmente pienamente funzionante
		if (ctx.type === "noderemoved" && Date.now() - lastLoad < 1000) {
			return ctx; // ignora
		}
		if (
			[
				"nodecreated",
				"noderemoved",
				"connectioncreated",
				"connectionremoved",
				"process",
				// "translate",
				// "rendernode",
			].includes(ctx.type)
		) {
			// console.log("ctx after", ctx.type);
			const conn = editor.getConnections();
			// console.log("connessioni", conn);
			saveDebounced();
		}
		return ctx;
	});

	// carica grafo corrente (se deviceId presente)
	if (route.params.deviceId) {
		lastLoad = Date.now();
		// console.log("carico deviceId", lastLoad);
		await loadGraph(Number(route.params.deviceId));
	}

	window.addEventListener(
		"ai-editor-command",
		onAiEditorCommand as EventListener,
	);
	ready = true;

	// Segna globalmente che l'editor è pronto
	(window as any).__editor_ready = true;

	// evitiamo che ai-editor-command venga chiamato prima che l'editor sia pronto
	window.dispatchEvent(
		new CustomEvent("editor-ready", {
			detail: { deviceId: route.params.deviceId },
		}),
	);

	// processa coda
	while (pending.length) await handleAIResult(pending.shift());
});

onBeforeUnmount(() => {
	window.removeEventListener(
		"ai-editor-refresh",
		onAiEditorRefresh as EventListener,
	);
	window.removeEventListener(
		"ai-editor-command",
		onAiEditorCommand as EventListener,
	);

	// ❌ resetta il flag globale
	(window as any).__editor_ready = false;
	destroy?.();
});

/* se cambia deviceId nell’URL ricarica il grafo */
watch(
	() => route.params.deviceId,
	async (newId, oldId) => {
		if (!newId || newId === oldId) return;

		loadingGraph = true;
		editor.clear();
		await nextTick();
		await loadGraph(Number(newId));
		loadingGraph = false;
	},
);
</script>

<style scoped>
.tour-pin {
	position: absolute;
	top: calc(56px + 24px); /* sotto il breadcrumb (56px) di ~24px */
	left: 25%;
	transform: translateX(-50%); /* centrato nel canvas */
	width: 1px;
	height: 1px;
	pointer-events: none;
}
/* ───────── HEADER editor ─────────────────────────────────────────── */
.breadcrumb-actions {
	display: flex;
	align-items: center;
	gap: 12px; /* distanza tra i due bottoni */
}

.toggle-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 6px; /* area clic/hover */
	border-radius: 8px;
	border: 1px solid transparent;
	background: transparent;
	color: var(--bc-fg);
	cursor: pointer;
	transition: background 0.15s ease, border-color 0.15s ease,
		box-shadow 0.15s ease, transform 0.06s ease;
	width: max-content; /* come avevi */
}

.toggle-icon :deep(svg),
.toggle-icon svg {
	width: 20px;
	height: 20px;
	stroke: currentColor;
	fill: none;
	stroke-width: 1.75;
}

.toggle-icon:hover {
	background: var(--bc-hover);
	border-color: var(--bc-border);
	box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.04);
	transform: translateY(-1px);
}
.toggle-icon:active {
	background: var(--bc-active);
	transform: translateY(0);
}

/* Focus accessibile, non il contorno blu del browser */
.toggle-icon:focus-visible {
	outline: 2px solid #00a7a7;
	outline-offset: 2px;
}

.toggle-icon[aria-pressed="true"],
.toggle-icon.is-active {
	background: var(--bc-hover);
	border-color: var(--bc-border);
}

.breadcrumb {
	position: sticky;
	top: 0;
	z-index: 10;
	height: 56px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px; /* spazio tra titolo e bottone */
	padding: 0 24px;
	background: #0d0d0d;
	font: 600 18px/56px "Inter", sans-serif;
	/* border-bottom: 2px solid transparent; */
	border-image: linear-gradient(to bottom, #535454, #1d3a3a) 1;

	--bc-bg: #0d0d0d;
	--bc-fg: #e6e6e6;
	--bc-hover: color-mix(in srgb, white 8%, var(--bc-bg)); /* overlay 8% */
	--bc-active: color-mix(in srgb, black 35%, var(--bc-bg)); /* click */
	--bc-border: color-mix(in srgb, white 12%, var(--bc-bg));
}

/* titolo: occupa lo spazio disponibile, si accorcia con ellissi */
.breadcrumb span {
	flex: 1 1 auto;
	min-width: 0; /* <-- fondamentale per far funzionare ellissi in flex */
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	padding-left: 4px;
}
/* mobile padding come da DevicesList */
/* @media (max-width: 1023px) {
		.breadcrumb {
			padding: 0 45px;
		}
	} */

/* bottone “Esci” */
.btn-exit {
	flex: 0 0 auto;
	padding: 8px 16px;
	background: #008f8f;
	border: 0;
	border-radius: 4px;
	color: #fff;
	cursor: pointer;
	font: 600 14px/1 "Inter", sans-serif;
	transition: background 0.15s;
}

.btn-exit:hover {
	background: #00a7a7;
}
.btn-exit:active {
	background: #007777;
}

@media (max-width: 1023px) {
	.breadcrumb {
		padding: 0 45px; /* un po’ meno padding su mobile */
		font-size: 16px; /* titolo leggermente più piccolo */
	}
	/* .btn-exit {
			padding: 6px 12px;
			font-size: 13px;
		} */
}

/* .editor-container :deep(.node) {
		opacity: 0;
		position: relative;
		padding-top: 1.5rem;
	} */

.editor-container :deep(.node) {
	position: relative;
	padding-top: 1.5rem;
	background: transparent; /* nessuno sfondo di base */
	box-shadow: none;
	border: 0;
}

.editor-container :deep(.node:hover),
.editor-container :deep(.node:active),
.editor-container :deep(.node:focus-visible) {
	background-color: rgba(130, 153, 255, 0.8);
	opacity: 0.7;
	/* border-radius: 236px !important; */
	box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
	/* transform: scale(0.5rem); */
	transition: opacity 0.3s ease;
	z-index: 1;
}

.editor-container :deep(.node) .title {
	position: relative;
	display: block;
	color: #4856db !important;
	font-family: sans-serif !important;
	font-size: 1.5rem !important; /* Usiamo rem per una migliore scalabilità */
	font-weight: bold;
	text-align: center;
	padding: 0.5rem 0.75rem !important;
	background-color: white;
	border-radius: 4px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	transform: translateY(-50%); /* Sostituisce il margin-top negativo */
	width: max-content;
	max-width: 100%;
	margin: 0 auto !important;
	z-index: 2;
	pointer-events: none;
}

.editor-container :deep(.node .input),
.editor-container :deep(.node .output),
.editor-container :deep(.node .control),
.editor-container :deep(.node .socket) {
	opacity: 0;
	visibility: hidden;
	transition: opacity 0.18s ease;
}

/* 4) In hover mostra contorno + IO (l’effetto blu che avevi) */
/* .editor-container :deep(.node:hover),
	.editor-container :deep(.node:active),
	.editor-container :deep(.node:focus-visible) {
		background-color: rgba(130, 153, 255, 0.12);
		outline: 2px solid rgba(130, 153, 255, 0.7);
		outline-offset: 0;
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
		transition: background-color 0.2s ease, box-shadow 0.2s ease;
		z-index: 1;
	} */

/* Nodi selezionati/trascinati: niente giallo,
   MA solo quando NON sono in hover */
@media (pointer: fine) {
	.editor-container :deep(.node.selected:not(:hover)),
	.editor-container :deep(.node.dragging:not(:hover)),
	.editor-container :deep(.node.selected:not(:hover) .content),
	.editor-container :deep(.node.dragging:not(:hover) .content),
	.editor-container :deep(.node.selected:not(:hover) .inner),
	.editor-container :deep(.node.dragging:not(:hover) .inner),
	.editor-container :deep(.node.selected:not(:hover) .main),
	.editor-container :deep(.node.dragging:not(:hover) .main) {
		background: transparent !important;
		box-shadow: none !important;
		outline: none !important;
		border-color: transparent !important;
	}

	/* In hover vuole prevalere lo stile blu */
	.editor-container :deep(.node.selected:hover),
	.editor-container :deep(.node.dragging:hover) {
		background-color: rgba(130, 153, 255, 0.8) !important;
		opacity: 0.7;
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
	}
}

.editor-container :deep(.node:hover .input),
.editor-container :deep(.node:hover .output),
.editor-container :deep(.node:hover .control),
.editor-container :deep(.node:hover .socket) {
	opacity: 1;
	visibility: visible;
}

/* ───────── Mobile / touch (pointer:coarse) ───────── */
/* mostra IO solo quando il nodo è selezionato; evidenzia con il blu */
@media (pointer: coarse) {
	/* IO di default nascosti */
	.editor-container :deep(.node .input),
	.editor-container :deep(.node .output),
	.editor-container :deep(.node .control),
	.editor-container :deep(.node .socket) {
		opacity: 0;
		visibility: hidden;
		transition: opacity 0.18s ease;
	}
	/* IO visibili sul nodo selezionato */
	.editor-container :deep(.node.selected .input),
	.editor-container :deep(.node.selected .output),
	.editor-container :deep(.node.selected .control),
	.editor-container :deep(.node.selected .socket),
	.editor-container :deep(.node.dragging .input),
	.editor-container :deep(.node.dragging .output),
	.editor-container :deep(.node.dragging .control),
	.editor-container :deep(.node.dragging .socket) {
		opacity: 1;
		visibility: visible;
	}
	/* niente giallo; usa il blu come feedback di selezione/drag */
	.editor-container :deep(.node.selected),
	.editor-container :deep(.node.dragging),
	.editor-container :deep(.node.selected .content),
	.editor-container :deep(.node.dragging .content) {
		background-color: rgba(130, 153, 255, 0.8) !important;
		outline: 2px solid rgba(130, 153, 255, 0.6) !important;
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1) !important;
	}
}

.editor-container :deep(.node) .output {
	margin-top: 0.5rem; /* Spazio tra titolo e output */
}

.editor-container :deep(.node) .output-title,
.editor-container :deep(.node) .input-title {
	font-size: 38px !important;
}

.editor-container {
	position: fixed;
	top: 0;
	left: var(--sb-offset); /* 👈 offset dinamico */
	width: calc(100% - var(--sb-offset)); /* larghezza residua  */
	bottom: 0;
	overflow: hidden;
	transition: left 0.25s ease, width 0.25s ease;
}

.editor-container :deep(.lcd-readonly) {
	display: none;
}

/* Animazioni per il loader */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.5s ease, transform 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
	opacity: 0;
	transform: translateY(-20px);
}

.loader-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 1000;
}

.loader-content {
	background: #008f8f;
	padding: 20px 30px;
	border-radius: 8px;
	display: flex;
	align-items: center;
	gap: 15px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.loader-spinner {
	border: 3px solid rgba(0, 0, 0, 0.1);
	border-radius: 50%;
	border-top: 3px solid #4856db;
	width: 24px;
	height: 24px;
	animation: spin 1s linear infinite;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.rete-background {
	position: absolute;
	top: 56px; /* = altezza breadcrumb */
	left: 0;
	right: 0;
	bottom: 0;
	background-color: #f0f4f8; /* Colore di sfondo principale */
	background-image: radial-gradient(#1d3450 1px, transparent 1px),
		/* Pattern a puntini */ radial-gradient(#d7e0eb 1px, transparent 1px); /* Doppio layer per effetto profondità */
	background-size: 30px 30px;
	background-position: 0 0, 15px 15px;
	overflow: auto;
}

/* Stili per mobile */
@media (max-width: 768px) {
	.rete-background {
		background-size: 20px 20px; /* Pattern più piccolo su mobile */
	}
}

/* ───────── overlay ───────── */
.modal-mask {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.55); /* più scuro */
	backdrop-filter: blur(3px); /* leggera sfocatura */
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 9999;
}

/* ───────── finestra ───────── */
.modal {
	min-width: 340px;
	max-width: 90%;
	background: #0d0d0d; /* nero sidebar */
	color: #fff;
	padding: 28px 32px;
	border-radius: 8px;
	box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35);
	animation: pop 0.25s ease-out;
}
@keyframes pop {
	from {
		opacity: 0;
		transform: scale(0.92);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

.modal h3 {
	margin: 0 0 18px;
	font: 600 18px/1.2 "Inter", sans-serif;
}

/* ───────── input ───────── */
.modal-input {
	width: 100%;
	padding: 10px 12px;
	border: 1px solid #444;
	border-radius: 5px;
	background: #1a1a1a;
	color: #eee;
	font-family: inherit;
	transition: border 0.2s;
}
.modal-input:focus {
	outline: none;
	border-color: #008f8f;
}

/* ───────── actions ───────── */
.modal-actions {
	margin-top: 26px;
	display: flex;
	justify-content: flex-end;
	gap: 12px;
}
.modal-actions button {
	min-width: 88px;
	padding: 8px 16px;
	font: 600 14px/1 "Inter", sans-serif;
	border-radius: 4px;
	cursor: pointer;
	color: #fff;
	background: #008f8f; /* colore di base per TUTTI */
	border: 1px solid #008f8f;
	transition: background 0.15s;
}

.modal-actions button:hover {
	background: #00a7a7; /* un filo più chiaro */
}

.modal-actions button:active {
	background: #007777; /* un filo più scuro */
}
</style>
